/**
 * Step-level presentation overrides resolver for EDITOR PREVIEW (v2.19.0).
 *
 * Mirror of the runtime resolver in web-shell
 * (`web-shell/src/engine/utils/presentationOverrides.ts`) — web-shell is a
 * standalone workspace, so the ~60 lines of pure merge logic are mirrored here
 * instead of imported. Keep BOTH in sync when the merge semantics change:
 * sparse merge, base → standard → active profile, `0` is a valid override,
 * override specs are non-recursive.
 */

export type PreviewPresentationProfile = 'base' | 'standard' | 'phone' | 'tablet' | 'laptop';

const OVERRIDES_KEY = 'presentationOverrides';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeOverride(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  let result = base;
  let changed = false;

  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;

    const baseValue = result[key];
    let nextValue: unknown = value;
    if (isPlainObject(value) && isPlainObject(baseValue)) {
      nextValue = { ...baseValue };
      let subChanged = false;
      for (const [subKey, subValue] of Object.entries(value)) {
        if (subValue === undefined) continue;
        if (baseValue[subKey] !== subValue) {
          (nextValue as Record<string, unknown>)[subKey] = subValue;
          subChanged = true;
        }
      }
      if (!subChanged) nextValue = baseValue;
    }

    if (nextValue !== baseValue) {
      if (!changed) {
        result = { ...result };
        changed = true;
      }
      result[key] = nextValue;
    }
  }

  return changed ? result : base;
}

function resolveNode(node: unknown, profile: Exclude<PreviewPresentationProfile, 'base'>): unknown {
  if (Array.isArray(node)) {
    let changed = false;
    const next = node.map((item) => {
      const resolved = resolveNode(item, profile);
      if (resolved !== item) changed = true;
      return resolved;
    });
    return changed ? next : node;
  }

  if (!isPlainObject(node)) return node;

  let result: Record<string, unknown> = node;
  let changed = false;

  for (const [key, value] of Object.entries(node)) {
    if (key === OVERRIDES_KEY) continue;
    const resolved = resolveNode(value, profile);
    if (resolved !== value) {
      if (!changed) {
        result = { ...node };
        changed = true;
      }
      result[key] = resolved;
    }
  }

  const overrides = node[OVERRIDES_KEY];
  if (isPlainObject(overrides)) {
    const standard = overrides.standard;
    const active = profile !== 'standard' ? overrides[profile] : undefined;
    let merged = result;
    if (isPlainObject(standard)) merged = mergeOverride(merged, standard);
    if (isPlainObject(active)) merged = mergeOverride(merged, active);
    if (merged !== result) {
      result = merged;
      changed = true;
    }
  }

  return changed ? result : node;
}

/**
 * Resolves presentationOverrides on a single StepNode for preview rendering.
 * 'base' returns the step untouched (classic editing view).
 */
export function applyStepPresentationOverrides<T extends object>(
  step: T,
  profile: PreviewPresentationProfile,
): T {
  if (profile === 'base') return step;
  return resolveNode(step, profile) as T;
}
